import { apiService } from '@/services/core/api.service';
import { comprimirImagen } from '@/shared/lib/imagenes';

// ─── Enums ────────────────────────────────────────────────────────────────────

export type ToolStatus =
  | 'good'
  | 'new_equipment'
  | 'damaged'
  | 'missing'
  | 'in_maintenance'
  | 'decommissioned';

export type MovementType =
  | 'assignment'
  | 'reassignment'
  | 'return'
  | 'decommission';

/** Motivos de baja aceptados por POST /almacen/herramientas/:id/dar-baja */
export type DecommissionReason =
  | 'theft'
  | 'loss'
  | 'irreparable_damage'
  | 'end_of_life';

export interface DecommissionPayload {
  reason: DecommissionReason;
  notes?: string;
  evidenceUrl1?: string;
  evidenceUrl2?: string;
  evidenceUrl3?: string;
}

/** Estado destino al reactivar (dar de alta) una herramienta dada de baja */
export type ReactivationStatus = 'good' | 'new_equipment';

export interface ReactivatePayload {
  status: ReactivationStatus;
  notes?: string;
}

// ─── Herramientas types ───────────────────────────────────────────────────────

export interface ToolResponseDto {
  id: string;
  name: string;
  serialCode: string;
  internalCode: string | null;
  status: ToolStatus;
  categoryId: string;
  categoryName: string;
  warehouseId: string;
  warehouseName: string;
  photoUrl1: string | null;
  photoUrl2: string | null;
  photoUrl3: string | null;
  maintenanceIntervalType: 'calendar' | 'usage_hours' | null;
  maintenanceIntervalValue: number | null;
  accumulatedUsageHours: number;
  currentMovementId: string | null;
  /**
   * Cómo va la aceptación de la asignación vigente (CR-03).
   *
   * La API lo manda desde que existe el flujo de aprobación, pero no estaba
   * declarado, así que se perdía al leer la respuesta: por eso el listado
   * mostraba al nuevo responsable sin avisar que todavía no acepta.
   *
   * Nulo en las asignaciones anteriores al flujo.
   */
  currentApprovalStatus?: 'pending' | 'approved' | 'rejected' | null;
  /** A quien se le asigno y todavia no acepta (CR-03). */
  pendingResponsibleUserName?: string | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  projectId: string | null;
  projectName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedToolsDto {
  data: ToolResponseDto[];
  total: number;
  page: number;
  limit: number;
}

export interface ToolFilters {
  categoryId?: string;
  warehouseId?: string;
  status?: ToolStatus;
  page?: number;
  limit?: number;
}

export interface CreateToolPayload {
  name: string;
  serialCode: string;
  status?: ToolStatus;
  categoryId: string;
  warehouseId: string;
  maintenanceIntervalType?: 'calendar' | 'usage_hours';
  maintenanceIntervalValue?: number;
}

export type UpdateToolPayload = Partial<CreateToolPayload>;

// ─── Categorías types ─────────────────────────────────────────────────────────

export interface CategoryApiResponse {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryPayload {
  name: string;
  description?: string;
}

export type UpdateCategoryPayload = Partial<CreateCategoryPayload>;

// ─── Movimientos types ────────────────────────────────────────────────────────

export interface MovimientoApiResponse {
  id: string;
  herramientaId: string;
  type: MovementType;
  projectId: string | null;
  responsibleUserId: string | null;
  warehouseId: string | null;
  evidenceUrl1: string | null;
  evidenceUrl2: string | null;
  evidenceUrl3: string | null;
  notes: string | null;
  cycleUsageHours: number | null;
  resultingStatus: ToolStatus | null;
  createdAt: string;
}

export interface PaginatedMovimientosResponse {
  data: MovimientoApiResponse[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AsignarPayload {
  projectId: string;
  responsibleUserId: string;
  warehouseId: string;
  evidenceUrl1?: string;
  evidenceUrl2?: string;
  evidenceUrl3?: string;
  notes?: string;
}

export interface ReasignarPayload {
  projectId?: string;
  responsibleUserId?: string;
  warehouseId?: string;
  evidenceUrl1?: string;
  evidenceUrl2?: string;
  evidenceUrl3?: string;
  notes?: string;
}

export interface DevolverPayload {
  resultingStatus: ToolStatus;
  warehouseId: string;
  cycleUsageHours?: number;
  evidenceUrl1?: string;
  evidenceUrl2?: string;
  evidenceUrl3?: string;
  notes?: string;
}

export interface MovimientosQueryParams {
  page?: number;
  limit?: number;
}

// ─── Upload types ─────────────────────────────────────────────────────────────

export interface UploadFotoResponse {
  url: string;
}

// ─── Herramientas service ─────────────────────────────────────────────────────

export const herramientasApi = {
  findAll: (filters?: ToolFilters): Promise<PaginatedToolsDto> => {
    const params = new URLSearchParams();
    if (filters?.categoryId) params.set('categoryId', filters.categoryId);
    if (filters?.warehouseId) params.set('warehouseId', filters.warehouseId);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.page !== undefined) params.set('page', String(filters.page));
    if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return apiService.request<PaginatedToolsDto>(`/almacen/herramientas${qs ? `?${qs}` : ''}`);
  },

  findOne: (id: string): Promise<ToolResponseDto> =>
    apiService.request<ToolResponseDto>(`/almacen/herramientas/${id}`),

  create: (dto: CreateToolPayload): Promise<ToolResponseDto> =>
    apiService.request<ToolResponseDto>('/almacen/herramientas', {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  update: (id: string, dto: UpdateToolPayload): Promise<ToolResponseDto> =>
    apiService.request<ToolResponseDto>(`/almacen/herramientas/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),

  remove: (id: string): Promise<void> =>
    apiService.request<void>(`/almacen/herramientas/${id}`, {
      method: 'DELETE',
    }),

  /** POST /almacen/herramientas/:id/dar-baja — marca la herramienta como decommissioned (204) */
  decommission: (id: string, dto: DecommissionPayload): Promise<void> =>
    apiService.request<void>(`/almacen/herramientas/${id}/dar-baja`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  /** POST /almacen/herramientas/:id/dar-alta — reactiva una herramienta dada de baja (204) */
  reactivate: (id: string, dto: ReactivatePayload): Promise<void> =>
    apiService.request<void>(`/almacen/herramientas/${id}/dar-alta`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),
};

// ─── Categorías service ───────────────────────────────────────────────────────

export const categoriasApi = {
  findAll: (): Promise<CategoryApiResponse[]> =>
    apiService.request<CategoryApiResponse[]>('/almacen/categorias'),

  findOne: (id: string): Promise<CategoryApiResponse> =>
    apiService.request<CategoryApiResponse>(`/almacen/categorias/${id}`),

  create: (dto: CreateCategoryPayload): Promise<CategoryApiResponse> =>
    apiService.request<CategoryApiResponse>('/almacen/categorias', {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  update: (id: string, dto: UpdateCategoryPayload): Promise<CategoryApiResponse> =>
    apiService.request<CategoryApiResponse>(`/almacen/categorias/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),

  remove: (id: string): Promise<void> =>
    apiService.request<void>(`/almacen/categorias/${id}`, {
      method: 'DELETE',
    }),
};

// ─── Movimientos service ──────────────────────────────────────────────────────

export const movimientosApi = {
  asignar: (id: string, dto: AsignarPayload): Promise<MovimientoApiResponse> =>
    apiService.request<MovimientoApiResponse>(`/almacen/herramientas/${id}/asignar`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  reasignar: (id: string, dto: ReasignarPayload): Promise<MovimientoApiResponse> =>
    apiService.request<MovimientoApiResponse>(`/almacen/herramientas/${id}/reasignar`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  devolver: (id: string, dto: DevolverPayload): Promise<MovimientoApiResponse> =>
    apiService.request<MovimientoApiResponse>(`/almacen/herramientas/${id}/devolver`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  findMovimientos: (
    id: string,
    params?: MovimientosQueryParams,
  ): Promise<PaginatedMovimientosResponse> => {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.append('page', String(params.page));
    if (params?.limit !== undefined) query.append('limit', String(params.limit));
    const qs = query.toString();
    return apiService.request<PaginatedMovimientosResponse>(
      `/almacen/herramientas/${id}/movimientos${qs ? `?${qs}` : ''}`,
    );
  },
};

// ─── Upload service ───────────────────────────────────────────────────────────

export const herramientasUploadApi = {
  /**
   * POST /almacen/upload/foto
   * Multipart/form-data with field "file". Uses fetch directly so the browser
   * sets the Content-Type header with the correct multipart boundary — the
   * apiService always injects "Content-Type: application/json" which would
   * break multipart parsing on the server.
   */
  uploadFoto: async (file: File): Promise<UploadFotoResponse> => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL as string;
    const token =
      window.location.pathname.startsWith('/proveedores')
        ? localStorage.getItem('supplier_access_token')
        : localStorage.getItem('access_token');

    const formData = new FormData();
    formData.append('file', await comprimirImagen(file));

    const response = await fetch(`${baseUrl}/almacen/upload/foto`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as {
        message?: string;
        error?: string;
      };
      throw {
        message: errorData.message ?? 'Error al subir la foto',
        error: errorData.error ?? 'Unknown',
        statusCode: response.status,
      };
    }

    return response.json() as Promise<UploadFotoResponse>;
  },
};
